import classnames from 'classnames'

import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton'
import Spinner from 'embed/components/LoadingIndicator/Spinner'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { configShouldUseCustomBackgroundSelector } from 'embed/selectors/config'
import { LifeInsuranceOffer } from 'embed/constants/models'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'
import { MultiProductState } from 'ui/multi-product/reducers'
import { AcceptAndContinueLanguage } from '../Application/AcceptAndContinueLanguage'
import { UpdateLeadFailed } from '../common/UpdateLeadFailed'
import { BackButton } from './BackButton'
import { CustomizeForm } from './CustomizeForm'
import { DisclaimerBox } from './DisclaimerBox'
import { PersonalInfoFields } from './PersonalInfoFields'
import { SelectedOffer } from './SelectedOffer'
import { getOpacityColor } from 'embed/utils'
import {
  BESTOW_UUID_DEV,
  BESTOW_UUID_PROD,
  HAVEN_SIMPLE_UUID_DEV,
  HAVEN_SIMPLE_UUID_PROD,
  HAVEN_TERM_UUID_DEV,
  HAVEN_TERM_UUID_PROD,
  LEGAL_GENERAL_UUID_DEV,
  LEGAL_GENERAL_UUID_PROD,
  THE_STANDARD_UUID_DEV,
  THE_STANDARD_UUID_PROD,
} from 'ui/multi-product/constants'

const applicationStyles = require('embed/styles/application')
const styles = require('./styles')
const ClockIcon = require('./clock-icon.svg')

interface StoreProps {
  enableCustomBackground: boolean
}

interface OwnProps {
  coverageAmount?: number
  duration?: number
  isValid: boolean
  loadingCustomizedOffer: boolean
  loadingLeadUpdate: boolean
  noOfferFound: boolean
  onApplyClick: () => void
  onBackButtonClick: () => void
  onCoverageAmountChange: (fieldName: string, value: string) => void
  onDurationChange: (fieldName: string, value: string) => void
  onRequestACallClick: () => void
  selectedOffer: LifeInsuranceOffer
  customizeable: boolean
  requestACall: boolean
  updateLeadFailed: boolean
}

type Props = OwnProps & StoreProps

const LifeInsurancePersonalInfoContainer: React.SFC<Props> = ({
  coverageAmount,
  duration,
  enableCustomBackground,
  isValid,
  loadingCustomizedOffer,
  loadingLeadUpdate,
  noOfferFound,
  onApplyClick,
  onBackButtonClick,
  onCoverageAmountChange,
  onDurationChange,
  onRequestACallClick,
  selectedOffer,
  customizeable,
  requestACall,
  updateLeadFailed,
}) => {
  let selectedOfferStatus
  if (loadingCustomizedOffer) {
    selectedOfferStatus = <Loading />
  } else if (noOfferFound) {
    selectedOfferStatus = <NoOfferFound />
  }

  const containerStyle = enableCustomBackground ? { backgroundColor: 'unset' } : {}

  return (
    <ThemeConsumer>
      {({ colorBorder, colorSecondary, borderRadiusSmall }) => (
        <div
          className={classnames(
            styles.container,
            css`
              border-color: ${colorBorder};
              border-radius: ${borderRadiusSmall};
            `,
          )}
          style={containerStyle}
        >
          <div className={styles.appBar}>
            <div className={styles.message}>
              <h1>Good Choice! Let's Get You Insurance</h1>
              <div className={styles.selectedOfferMobileContainer}>
                <SelectedOffer offer={selectedOffer} status={selectedOfferStatus}>
                  {customizeable && (
                    <CustomizeForm
                      coverageAmount={coverageAmount}
                      duration={duration}
                      onCoverageAmountChange={onCoverageAmountChange}
                      onDurationChange={onDurationChange}
                      offer={selectedOffer}
                    />
                  )}
                </SelectedOffer>
              </div>
            </div>
          </div>
          <div className={styles.wrapper}>
            <div className={styles.personalInfoContainer}>
              <BackButton className={styles.backButton} onClick={onBackButtonClick} />
              <h2>Next Steps</h2>
              {selectedOffer.details.onlineAppAvailable &&
                [
                  HAVEN_SIMPLE_UUID_DEV,
                  HAVEN_SIMPLE_UUID_PROD,
                  HAVEN_TERM_UUID_DEV,
                  HAVEN_TERM_UUID_PROD,
                  LEGAL_GENERAL_UUID_DEV,
                  LEGAL_GENERAL_UUID_PROD,
                  BESTOW_UUID_PROD,
                  BESTOW_UUID_DEV,
                  THE_STANDARD_UUID_DEV,
                  THE_STANDARD_UUID_PROD,
                ].includes(selectedOffer.partner.uuid) && (
                  <div
                    className={classnames(
                      styles.messagePillContainer,
                      css`
                        svg path {
                          fill: ${colorSecondary} !important;
                        }
                      `,
                    )}
                    style={{ backgroundColor: getOpacityColor(colorSecondary, 5) }}
                  >
                    <ClockIcon />
                    <p>Get your policy in minutes!</p>
                  </div>
                )}
              <h1>Good Choice! Let's Get You Insurance</h1>
              <PersonalInfoFields />
              <DisclaimerBox />
              <div className={styles.buttonWrapper}>
                {requestACall || noOfferFound ? (
                  <SubmitButtonRequestACall
                    onRequestACallClick={onRequestACallClick}
                    isValid={isValid}
                    loading={loadingLeadUpdate}
                  />
                ) : (
                  <SubmitButtonApply
                    onApplyClick={onApplyClick}
                    isValid={!loadingCustomizedOffer && isValid}
                    loading={loadingLeadUpdate}
                  />
                )}
              </div>
              {updateLeadFailed && <UpdateLeadFailed />}
              <AcceptAndContinueLanguage
                className={classnames(applicationStyles.consentWithButton, styles.consentWrapper)}
              />
            </div>
            <div className={styles.offerInfoContainer}>
              <div className={styles.selectedOfferDesktopContainer}>
                <SelectedOffer offer={selectedOffer} status={selectedOfferStatus}>
                  {customizeable && (
                    <CustomizeForm
                      coverageAmount={coverageAmount}
                      duration={duration}
                      onCoverageAmountChange={onCoverageAmountChange}
                      onDurationChange={onDurationChange}
                      offer={selectedOffer}
                    />
                  )}
                </SelectedOffer>
              </div>
            </div>
          </div>
        </div>
      )}
    </ThemeConsumer>
  )
}

const Loading: React.SFC = () => (
  <div className={styles.spinnerContainer}>
    <Spinner className={styles.spinner} />
  </div>
)

const NoOfferFound: React.SFC = () => (
  <ThemeConsumer>
    {({ colorTextSecondary }) => (
      <div className={styles.noOffer}>
        <h3 className={styles.noOfferTitle}>We were unable to get a quote for that combination.</h3>{' '}
        <p
          className={classnames(
            styles.noOfferText,
            css`
              color: ${colorTextSecondary};
            `,
          )}
        >
          Please try again or submit your info and we’ll reach out to you.
        </p>
      </div>
    )}
  </ThemeConsumer>
)

const SubmitButtonApply = ({ isValid, onApplyClick, loading }) => (
  <ApplicationSubmitButton isValid={isValid} loading={loading} onSubmit={onApplyClick}>
    <span className={applicationStyles.consentButtonLabel}>Agree and Continue</span>
  </ApplicationSubmitButton>
)

const SubmitButtonRequestACall = ({ isValid, onRequestACallClick, loading }) => (
  <ApplicationSubmitButton isValid={isValid} loading={loading} onSubmit={onRequestACallClick}>
    <span className={applicationStyles.consentButtonLabel}>Agree and Request a Call</span>
  </ApplicationSubmitButton>
)

const mapStateToProps = (state: MultiProductState) => ({
  enableCustomBackground: configShouldUseCustomBackgroundSelector(state),
})

export const LifeInsurancePersonalInfo = connect(mapStateToProps)(LifeInsurancePersonalInfoContainer)
